import type React from "react";
import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { ensureHttpsProtocol } from "simplydo/core/linkHref";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
} from "simplydo/react/ui/modal";

type ExternalLinkModalProps = {
  href: string;
  color?: string;
  onClose?: () => void;
  onFollow?: () => void;
} & (
  | { open: boolean; children?: never }
  | { children: React.ReactNode; open?: never }
);

export const ExternalLinkModal = ({
  children,
  color,
  open,
  href,
  onClose,
  onFollow,
}: ExternalLinkModalProps) => {
  const [controlledOpen, setControlledOpen] = useState(false);
  const { t } = useTranslation();

  const sureHref = useMemo(() => ensureHttpsProtocol(href || ""), [href]);

  return (
    <>
      <Modal
        size="small"
        open={open || controlledOpen}
        onClose={() => {
          onClose?.();
          setControlledOpen(false);
        }}
      >
        <ModalHeader>Warning</ModalHeader>
        <ModalContent>
          <ModalDescription>
            You are about to leave the safety of the SimplyDo platform to follow
            an external link. Please make sure that you can trust the linked
            resource before proceeding.
            <br />
            <br />
            Please make sure to never enter your login information anywhere but
            on the SimplyDo platform.
          </ModalDescription>
          <Divider />
          <ModalDescription>
            <b>{sureHref}</b>
          </ModalDescription>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              window.open(sureHref, "_blank");
              setControlledOpen(false);
              onFollow?.();
            }}
            color="secondary"
          >
            Open link in a new tab
          </Button>
          <Button
            onClick={() => {
              setControlledOpen(false);
              onClose?.();
            }}
          >
            {t("generic.cancel")}
          </Button>
        </ModalActions>
      </Modal>
      {children ? (
        <a
          onClick={(e) => {
            e.preventDefault();
            setControlledOpen(true);
          }}
          href={sureHref}
          style={{ color }}
        >
          {children}
        </a>
      ) : null}
    </>
  );
};
